/**
 * Projects/index.js — Project list screen (home page for authenticated users).
 * Fetches and displays all projects for the current user. Supports creating
 * new projects (via modal) and deleting existing ones with confirmation.
 */
import { useState, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
// ------------------------------------------------
import Frame from "Components/Frame";
// ------------------------------------------------
import { eraseProject, newProject } from "Redux/project/actions";
import { listProjects } from "Redux/projects/actions";
// ------------------------------------------------
import waitbar from "Media/waitbar.gif";
dayjs.extend(relativeTime);

// ------------------------------------------------
// ------------------------------------------------
const Projects = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [popup, setPopup] = useState(false);

  const { projects } = useSelector((state) => state.projects);
  const { uid } = useSelector((state) => state.user);

  useEffect(() => {
    if (uid) {
      dispatch(listProjects(uid));
    }
  }, [dispatch, uid]);

  const onNewProject = () => {
    setPopup(true);
  };

  if (!projects)
    return (
      <Frame>
        <img src={waitbar} alt="waitbar" />
      </Frame>
    );

  return (
    <Frame onNewProject={onNewProject}>
      {popup && (
        <NewProject
          onClose={() => {
            setPopup(false);
          }}
        />
      )}
      {projects.length ? (
        <table className="table is-striped is-hoverable">
          <thead>
            <tr>
              <th>Project</th>
              <th>Type</th>
              <th>Last Modified</th>
              <th>Since</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {projects.map((project) => (
              <tr
                className="is-clickable"
                key={project._id}
                onClick={() => {
                  // Clear stale project state before navigating to avoid
                  // briefly rendering the previous project's data.
                  dispatch({ type: "CLEAR_PROJECT" });
                  navigate(`/project/${project._id}/main`);
                }}
              >
                <td>{project.title}</td>
                <td style={{ textTransform: "capitalize" }}>
                  {project.projectType || "receivables"}
                </td>
                <td>{dayjs(project.modified).fromNow()}</td>
                <td>{dayjs(project.setup).fromNow()}</td>
                <td>
                  <span
                    className="icon is-small is-clickable"
                    onClick={(evt) => {
                      evt.stopPropagation();
                      const projectid = project._id;
                      if (
                        window.confirm(
                          "Are you sure you want to delete this project?"
                        )
                      ) {
                        dispatch(eraseProject(projectid));
                      }
                    }}
                  >
                    <i className="fa-duotone fa-trash-alt" />
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <div className="notification is-info is-light">
          You have no projects yet.
        </div>
      )}
    </Frame>
  );
};

export default Projects;

// ------------------------------------------------
// ------------------------------------------------
/** Modal dialog for creating a new project with a name and type (receivables/payables). */
const NewProject = ({ onClose }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [value, setValue] = useState("");
  const [projectType, setProjectType] = useState("receivables");
  const [generating, setGenerating] = useState(false);

  const setUpNew = () => {
    setGenerating(true);
    dispatch(
      newProject(value, projectType, (projectid) => {
        if (projectid) {
          navigate(`/project/${projectid}/main`);
        }
        setGenerating(false);
        onClose();
      })
    );
  };

  return (
    <div className="modal is-active">
      <div className="modal-background"></div>
      <div className="modal-card">
        <header className="modal-card-head convert">
          <div className="header">
            <p className="modal-card-title">New Project</p>
            <button className="delete" aria-label="close" onClick={onClose} />
          </div>
        </header>
        <section className="modal-card-body">
          <div className="field">
            <label className="label">Project Name</label>
            <div className="control">
              <input
                className="input"
                type="text"
                placeholder="Enter project name"
                onChange={(evt) => setValue(evt.target.value)}
              />
            </div>
          </div>
          <div className="field">
            <label className="label">Project Type</label>
            <div className="control">
              <label className="radio">
                <input
                  type="radio"
                  name="projectType"
                  checked={projectType === "receivables"}
                  onChange={() => setProjectType("receivables")}
                />{" "}
                Receivables
              </label>
              <label className="radio">
                <input
                  type="radio"
                  name="projectType"
                  checked={projectType === "payables"}
                  onChange={() => setProjectType("payables")}
                />{" "}
                Payables
              </label>
            </div>
          </div>
        </section>
        <footer className="modal-card-foot">
          <div className="buttons is-small">
            <button
              className={`button is-small is-primary is-light ${
                generating ? "is-loading" : ""
              }`}
              disabled={!value}
              onClick={setUpNew}
            >
              New Project
            </button>
            <button
              className="button is-small"
              disabled={generating}
              onClick={onClose}
            >
              Discard
            </button>
          </div>
        </footer>
      </div>
    </div>
  );
};
